import React, { useEffect, useMemo, useState } from 'react';
import {
  FileSpreadsheet,
  Zap,
  Activity,
  Calendar,
  CheckCircle2,
  ArrowLeft,
  TrendingUp,
  CalendarOff,
  Edit2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';
import { DayOffer } from '../Calculate/types';
import { bidToDayOffer, exportFileName } from '../Calculate/utils/naerasBid';
import EnergyChart from '../Calculate/components/shared/EnergyChart';
import HybridBarChart from '../Calculate/components/shared/HybridBarChart';
import HistoryCalendar from './components/HistoryCalendar';
import useNotify from '../../hooks/useNotify';
import { useAppDispatch } from '@/data/store/utils/redux';
import { exportNaerasBidsAsync, getNaerasBidsAsync } from '@/data/store/actions';
import { getAllNaerasBids, getNaerasBidsMap } from '@/data/store/selectors';

/** The export endpoint takes at most this many dates in one call. */
const MAX_EXPORT_DATES = 4;

/** Triggers the browser download of a blob under a given name. */
function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useNotify();

  const storedBids = useSelector(getAllNaerasBids);
  const bidsMap = useSelector(getNaerasBidsMap);

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [activeChartDate, setActiveChartDate] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  // The full bid history, newest first. Every day that has ever been
  // calculated has exactly one row.
  useEffect(() => {
    dispatch(
      getNaerasBidsAsync({ page_size: 100, sort_by: 'target_date', sort_dir: 'DESC' })
    )
      .unwrap()
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // All dates that have a stored bid
  const savedDates = useMemo(() => storedBids.map((bid) => bid.target_date), [storedBids]);

  // Open on the most recent calculated day.
  useEffect(() => {
    if (selectedDates.length > 0 || savedDates.length === 0) return;

    setSelectedDates([savedDates[0]]);
    setActiveChartDate(savedDates[0]);
  }, [savedDates, selectedDates.length]);

  const sortedSelectedDates = useMemo(() => [...selectedDates].sort(), [selectedDates]);

  // Bids are calculated for future days, so the calendar's ceiling is the
  // newest bid on file, not today.
  const maxSelectableDate = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    return savedDates.reduce((latest, date) => (date > latest ? date : latest), todayStr);
  }, [savedDates]);

  const activeOffers = useMemo(
    () =>
      sortedSelectedDates
        .map((d) => (bidsMap[d] ? bidToDayOffer(bidsMap[d]) : null))
        .filter((o): o is DayOffer => Boolean(o)),
    [sortedSelectedDates, bidsMap]
  );

  useEffect(() => {
    if (activeOffers.length === 0) return;

    if (!activeOffers.some((o) => o.date === activeChartDate)) {
      setActiveChartDate(activeOffers[0].date);
    }
  }, [activeOffers, activeChartDate]);

  // Offer currently plotted in the single-day view
  const currentOffer = activeOffers.find((o) => o.date === activeChartDate) ?? activeOffers[0];

  /** Downloads the official ΔΕΔΔΗΕ workbook for a set of days. */
  const exportDates = async (dates: string[]) => {
    if (dates.length === 0) {
      showToast({
        type: 'warning',
        title: 'Δεν υπάρχουν δεδομένα',
        message: 'Επιλέξτε τουλάχιστον μία ημέρα για εξαγωγή.',
      });
      return;
    }

    if (dates.length > MAX_EXPORT_DATES) {
      showToast({
        type: 'warning',
        title: 'Πολλές ημέρες',
        message: `Η εξαγωγή δέχεται έως ${MAX_EXPORT_DATES} ημέρες ανά αρχείο.`,
      });
      return;
    }

    setIsExporting(true);

    try {
      const blob = await dispatch(exportNaerasBidsAsync({ dates })).unwrap();

      downloadBlob(blob as Blob, exportFileName(dates));

      showToast({
        type: 'success',
        title: 'Εξαγωγή Ιστορικού σε Excel',
        message: `Εξήχθησαν επιτυχώς ${dates.length} ημέρες σε επίσημη μορφή ΔΕΔΔΗΕ.`,
      });
    } catch {
      // Reported by the global error handler.
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = () => exportDates(sortedSelectedDates);

  const handleExportSingleDay = (offer: DayOffer) => exportDates([offer.date]);

  // Aggregate KPIs for the selected period
  const totalPeriodHydro = activeOffers
    .reduce((acc, o) => {
      const sumAno = o.hourly.reduce((s, h) => s + h.mysAno, 0);
      const sumKato = o.hourly.reduce((s, h) => s + h.mysKato, 0);
      return acc + sumAno + sumKato;
    }, 0)
    .toFixed(1);

  const totalPeriodHybrid = activeOffers
    .reduce((acc, o) => acc + o.hybrid1st12h + o.hybrid2nd12h, 0)
    .toFixed(1);

  const totalPeriodEnergy = (Number(totalPeriodHydro) + Number(totalPeriodHybrid)).toFixed(1);

  const finalizedCount = activeOffers.filter((o) => o.isFinalized).length;
  /**
   * Locked days holding a newer Excel. Worth its own number on the card: those
   * days read as finished while the values on screen are still the old ones.
   */
  const pendingRevisionCount = activeOffers.filter((o) => o.hasPendingRevision).length;

  // Chart data for active offer
  const singleDayAnoData = (currentOffer?.hourly ?? []).map((h) => ({
    id: h.hour,
    name: `Ώρα ${h.hour.toString().padStart(2, '0')}`,
    value1: h.mysAno,
    value2: 0,
  }));

  const singleDayKatoData = (currentOffer?.hourly ?? []).map((h) => ({
    id: h.hour,
    name: `Ώρα ${h.hour.toString().padStart(2, '0')}`,
    value1: h.mysKato,
    value2: 0,
  }));

  const anoSeries = [
    {
      key: 'value1',
      name: 'Προσφορά ΜΥΗΣ Προεσπέρας',
      color: '#3b82f6',
      gradient: 'linear-gradient(to right, #3b82f6, #1d4ed8)',
    },
  ];

  const katoSeries = [
    {
      key: 'value1',
      name: 'Προσφορά ΜΥΗΣ Κάτω Προεσπέρας',
      color: '#10b981',
      gradient: 'linear-gradient(to right, #10b981, #059669)',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/naeras/hybrid')}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition shadow-xs"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Υπολογισμός Προσφοράς
              </button>
              <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-bold text-blue-800">
                Αρχείο Προσφορών
              </span>
            </div>
            <h1 className="mt-2 text-3xl font-bold text-gray-900">
              Ιστορικό Προσφορών ΔΕΗΑΝ – ΥΕΕ Ικαρίας
            </h1>
            <p className="mt-1 text-base text-gray-600">
              Προβολή γραφημάτων υπολογισμένης προσφοράς και εξαγωγή σε επίσημο Excel ΔΕΔΔΗΕ για οποιαδήποτε ημέρα ή περίοδο.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Export to Excel Button */}
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/25 transition hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Εξαγωγή σε Excel ΔΕΔΔΗΕ
              {activeOffers.length > 1 && ` (${activeOffers.length} ημέρες)`}
            </button>
          </div>
        </div>

        {/* Calendar Picker for Date & Range Selection */}
        <section>
          <HistoryCalendar
            selectedDates={selectedDates}
            onDatesChange={setSelectedDates}
            savedDates={savedDates}
            maxDate={maxSelectableDate}
          />
        </section>

        {activeOffers.length === 0 ? (
          <section className="flex flex-col items-center gap-3 rounded-3xl border border-amber-200 bg-amber-50/90 p-10 text-center shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <CalendarOff className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-900">
                Δεν υπάρχει υπολογισμένη προσφορά για τις επιλεγμένες ημέρες
              </p>
              <p className="mt-1 text-xs text-amber-800">
                {savedDates.length > 0
                  ? 'Επιλέξτε μια ημέρα με πράσινη κουκκίδα στο ημερολόγιο.'
                  : 'Δεν έχει υπολογιστεί καμία προσφορά ακόμη.'}
              </p>
            </div>
            {savedDates.length > 0 ? (
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                {savedDates.slice(0, 6).map((date) => (
                  <button
                    key={date}
                    onClick={() => setSelectedDates([date])}
                    className="rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-bold text-amber-900 transition hover:bg-amber-100"
                  >
                    {format(parseISO(date), 'dd/MM/yyyy')}
                  </button>
                ))}
              </div>
            ) : (
              <button
                onClick={() => navigate('/naeras/hybrid')}
                className="rounded-2xl bg-amber-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-amber-700"
              >
                Υπολογισμός Προσφοράς
              </button>
            )}
          </section>
        ) : (
          <>
        {/* Period Summary KPI Cards */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Card 1: Total Hydro */}
          <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-900/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">
                Υδροηλεκτρική Ενέργεια Περιόδου
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <Zap className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-black text-gray-900">{totalPeriodHydro}</span>
              <span className="text-xs font-semibold text-gray-400">MWh</span>
            </div>
            <span className="mt-2 text-xs font-semibold text-gray-500">
              ΜΥΗΣ Προεσπέρας & Κάτω Προεσπέρας
            </span>
          </div>

          {/* Card 2: Total Hybrid */}
          <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-900/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">
                Υβριδική Ενέργεια Περιόδου
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                <Activity className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-black text-gray-900">{totalPeriodHybrid}</span>
              <span className="text-xs font-semibold text-gray-400">MWh</span>
            </div>
            <span className="mt-2 text-xs font-semibold text-gray-500">
              {activeOffers.length * 2} x 12ωρα διαστήματα
            </span>
          </div>

          {/* Card 3: Grand Total */}
          <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-900/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">
                Συνολική Προσφορά Περιόδου
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <TrendingUp className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-black text-emerald-700">{totalPeriodEnergy}</span>
              <span className="text-xs font-semibold text-gray-400">MWh</span>
            </div>
            <span className="mt-2 text-xs font-semibold text-gray-500">
              {activeOffers.length > 0
                ? `Μ.Ο. ${(Number(totalPeriodEnergy) / activeOffers.length).toFixed(1)} MWh / ημέρα`
                : '0 MWh'}
            </span>
          </div>

          {/* Card 4: Finalized Status */}
          <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-5 shadow-xl shadow-gray-900/5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500">
                Κατάσταση Προσφορών
              </span>
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
                <CheckCircle2 className="h-4 w-4" />
              </div>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-black text-gray-900">
                {finalizedCount} / {activeOffers.length}
              </span>
              <span className="text-xs font-semibold text-gray-400">Οριστικές</span>
            </div>
            <span className="mt-2 text-xs font-semibold text-gray-500">
              {pendingRevisionCount > 0
                ? `${pendingRevisionCount} ${
                    pendingRevisionCount === 1 ? 'ημέρα' : 'ημέρες'
                  } με εκκρεμή αναθεώρηση`
                : finalizedCount === activeOffers.length && activeOffers.length > 0
                ? 'Όλες οι ημέρες έχουν οριστικοποιηθεί'
                : 'Περιλαμβάνονται υπολογισμένα προσχέδια'}
            </span>
          </div>
        </section>

        {/* 3 Charts Section (Matching PDF page 2 screenshot) */}
        <section className="space-y-6">
          {/* Day Selector Pills Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-3.5 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <Calendar className="h-4 w-4 text-blue-600" />
              <span>Προβολή Γραφημάτων ανά Ημέρα:</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {activeOffers.map((o) => {
                const isActive = currentOffer?.date === o.date;
                return (
                  <button
                    key={o.date}
                    onClick={() => setActiveChartDate(o.date)}
                    className={`rounded-xl px-4 py-1.5 text-xs font-bold transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 ring-2 ring-blue-600 ring-offset-1'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                  >
                    {o.dayLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grid Layout for the 3 Charts */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* 1. Hydro Proesperas Chart */}
            <div>
              <EnergyChart
                data={singleDayAnoData}
                title="Δήλωση Υδροηλεκτρικής ΜΥΗΣ Προεσπέρας"
                subtitle={
                  currentOffer?.mode === 'summer_mode'
                    ? `Ξηρή Περίοδος – Εκτός λειτουργίας (${currentOffer?.dayLabel})`
                    : `Υγρή Περίοδος – Προσφορά ΜΥΗΣ (${currentOffer?.dayLabel})`
                }
                series={anoSeries}
                height={260}
              />
            </div>

            {/* 2. Hydro Lower Proesperas Chart */}
            <div>
              <EnergyChart
                data={singleDayKatoData}
                title="Δήλωση Υδροηλεκτρικής ΜΥΗΣ Κάτω Προεσπέρας"
                subtitle={
                  currentOffer?.mode === 'summer_mode'
                    ? `Ξηρή Περίοδος – Εκτός λειτουργίας (${currentOffer?.dayLabel})`
                    : `Υγρή Περίοδος – Προσφορά ΜΥΗΣ (${currentOffer?.dayLabel})`
                }
                series={katoSeries}
                height={260}
              />
            </div>

            {/* 3. Hybrid 1st & 2nd 12h Bar Chart (Full width span, 2 bars in MWh) */}
            <div className="lg:col-span-2">
              <HybridBarChart
                first12hMwh={currentOffer?.hybrid1st12h ?? 0}
                second12hMwh={currentOffer?.hybrid2nd12h ?? 0}
                dayLabel={currentOffer?.dayLabel ?? ''}
                height={260}
              />
            </div>
          </div>
        </section>

        {/* Detailed Table Section for Selected Period */}
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Αναλυτικός Πίνακας Προσφορών Περιόδου
              </h3>
              <p className="text-xs text-gray-500">
                Επισκόπηση ανά ημέρα των δηλωθέντων ποσοτήτων ενέργειας και κατάστασης οριστικοποίησης.
              </p>
            </div>
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition shadow-xs"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
              Εξαγωγή Όλων ({activeOffers.length})
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-gray-200 bg-gray-50/70 font-semibold text-gray-600">
                <tr>
                  <th className="py-3 px-4">Ημερομηνία</th>
                  <th className="py-3 px-4">Περίοδος</th>
                  <th className="py-3 px-4 text-right">ΜΥΗΣ Προεσπ. (MWh)</th>
                  <th className="py-3 px-4 text-right">ΜΥΗΣ Κ. Προεσπ. (MWh)</th>
                  <th className="py-3 px-4 text-right">1ο 12ωρο (MWh)</th>
                  <th className="py-3 px-4 text-right">2ο 12ωρο (MWh)</th>
                  <th className="py-3 px-4 text-right">Σύνολο (MWh)</th>
                  <th className="py-3 px-4 text-center">Κατάσταση</th>
                  <th className="py-3 px-4 text-center">Ενέργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {activeOffers.map((offer) => {
                  const sumAno = offer.hourly.reduce((acc, h) => acc + h.mysAno, 0);
                  const sumKato = offer.hourly.reduce((acc, h) => acc + h.mysKato, 0);
                  const totalHydro = sumAno + sumKato;
                  const totalHybrid = offer.hybrid1st12h + offer.hybrid2nd12h;
                  const totalDaily = (totalHydro + totalHybrid).toFixed(1);
                  const isCurrent = offer.date === currentOffer?.date;

                  return (
                    <tr
                      key={offer.date}
                      className={`hover:bg-blue-50/40 transition-colors ${
                        isCurrent ? 'bg-blue-50/60 font-semibold' : ''
                      }`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{offer.dayLabel}</span>
                          <span className="text-gray-400 text-[11px]">({offer.date})</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`rounded-lg px-2 py-0.5 text-[11px] font-bold ${
                            offer.mode === 'winter_mode'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {offer.mode === 'winter_mode' ? 'Υγρή' : 'Ξηρή'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono">{sumAno.toFixed(1)}</td>
                      <td className="py-3 px-4 text-right font-mono">{sumKato.toFixed(1)}</td>
                      <td className="py-3 px-4 text-right font-mono">{offer.hybrid1st12h}</td>
                      <td className="py-3 px-4 text-right font-mono">{offer.hybrid2nd12h}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-gray-900">
                        {totalDaily}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {/* A locked day is not one single state: the nightly job
                            may have locked it unattended, it may have been
                            revised after the fact, and a newer Excel may still
                            be waiting — all three change what the numbers in
                            this row actually mean. */}
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              offer.isFinalized
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {offer.isFinalized
                              ? offer.revisedAt
                                ? 'Αναθεωρημένη'
                                : 'Οριστική'
                              : 'Προσχέδιο'}
                          </span>
                          {offer.isZeroBid ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800"
                              title="Δεν έγινε υπολογισμός — το σύστημα κλείδωσε μηδενική προσφορά"
                            >
                              Μηδενική προσφορά
                            </span>
                          ) : (
                            offer.autoFinalized && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-700"
                                title="Κλείδωσε από την αυτόματη εργασία των 23:30, χωρίς χρήστη"
                              >
                                Αυτόματη 23:30
                              </span>
                            )
                          )}
                          {offer.hasPendingRevision && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800"
                              title="Έχει ανέβει νεότερο Excel — ισχύει μόνο μετά από νέα οριστικοποίηση"
                            >
                              Εκκρεμεί αναθεώρηση
                            </span>
                          )}
                          {offer.isFinalized && offer.hybridCorrectionMwh !== 0 && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-800"
                              title="Διόρθωση υβριδικής από αναθεώρηση παλαιότερης ημέρας — διαφέρει από το αρχείο που ανέβηκε"
                            >
                              Διόρθωση {offer.hybridCorrectionMwh >= 0 ? '+' : ''}
                              {offer.hybridCorrectionMwh.toFixed(1)} MWh
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setActiveChartDate(offer.date)}
                            className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold transition ${
                              isCurrent
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Plot
                          </button>
                          <button
                            onClick={() => navigate(`/naeras/hybrid?date=${offer.date}`)}
                            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-blue-700 transition"
                            title="Αναθεώρηση Προσφοράς (Επεξεργασία)"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleExportSingleDay(offer)}
                            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-emerald-700 transition"
                            title="Εξαγωγή ημέρας σε Excel"
                          >
                            <FileSpreadsheet className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
          </>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
